/**
 * Module dependencies.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'app/components/ui/spinner';
import { Input } from 'app/components/ui/input';
import { Button } from 'app/components/atoms/button/button';
import { SectionHeader } from 'app/components/molecules/section-header/section-header';
import { cn } from 'app/lib/utils';
import { Landmark, Lock, ShieldAlert } from 'lucide-react';
import {
  PublicShareResult,
  usePublicMovementShare,
  usePublicMovementShareMeta
} from 'app/hooks/requests/transactions/use-movement-shares';

/**
 * Format a money amount in the account's currency.
 */

function money(value: string | number, coinId?: string | null): string {
  const amount = Number(value ?? 0);
  const currency = (coinId ?? 'eur').toUpperCase();

  try {
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/**
 * Format a date as a short local date.
 */

function shortDate(value: string | null): string {
  if (!value) {
    return '—';
  }

  const date = new Date(String(value).replace(' ', 'T'));

  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('pt-PT');
}

/**
 * Export `PublicMovementShare` — the anonymous, read-only movements page a share
 * link opens. No auth, no dashboard chrome.
 */

export function PublicMovementShare(): JSX.Element {
  const [translate] = useTranslation();
  const { token = '' } = useParams();
  const { data: meta, isLoading } = usePublicMovementShareMeta(token);
  const fetchMovements = usePublicMovementShare();
  const [pin, setPin] = useState('');
  const [result, setResult] = useState<PublicShareResult | null>(null);
  const autoFetched = useRef(false);

  // Public (no PIN) shares load immediately; PIN shares wait for the prompt.
  useEffect(() => {
    if (meta?.available && meta.requiresPin === false && !autoFetched.current) {
      autoFetched.current = true;
      fetchMovements.mutate({ token }, { onSuccess: setResult });
    }
  }, [meta, token, fetchMovements]);

  const submitPin = () => {
    fetchMovements.mutate({ token, pin }, { onSuccess: setResult });
  };

  const coinId = result?.account?.coinId ?? meta?.coinId;

  const columns = useMemo(() => {
    // Canonical column order (previous balance before amount), regardless of how
    // the share stored/selected its fields.
    const order = ['date', 'description', 'category', 'previousBalance', 'amount', 'balance'];
    const fields = result?.fields ?? meta?.fields ?? ['date', 'description', 'previousBalance', 'amount', 'balance'];

    return [...(fields as string[])]
      .sort((a, b) => order.indexOf(a) - order.indexOf(b))
      .map(field => ({
        key: field,
        label: translate(`movementShare.field.${field}`),
        numeric: ['amount', 'balance', 'previousBalance'].includes(field)
      }));
  }, [result?.fields, meta?.fields, translate]);

  if (isLoading) {
    return (
      <div className={'grid min-h-screen place-items-center bg-muted/30'}>
        <Spinner />
      </div>
    );
  }

  // Unavailable link (not found / revoked / expired).
  if (!meta?.available) {
    return (
      <Shell>
        <div className={'flex flex-col items-center gap-3 py-10 text-center'}>
          <ShieldAlert className={'h-10 w-10 text-muted-foreground'} />

          <h1 className={'text-lg font-semibold text-foreground'}>{translate('movementShare.unavailableTitle')}</h1>

          <p className={'max-w-sm text-sm text-muted-foreground'}>
            {translate(`movementShare.unavailable.${meta?.reason ?? 'not_found'}`)}
          </p>
        </div>
      </Shell>
    );
  }

  const locked = result?.reason === 'locked';
  const invalidPin = result?.reason === 'invalid_pin';
  const needPin = meta.requiresPin && !result?.ok;

  // PIN gate — deliberately shows no title or account name (nothing about the
  // account is revealed before the PIN is entered).
  if (needPin) {
    return (
      <div className={'grid min-h-screen place-items-center bg-muted/30 px-4'}>
        <div className={'w-full max-w-sm space-y-3 rounded-xl border border-border bg-card p-6 shadow-sm'}>
          <div className={'flex flex-col items-center gap-2 text-center'}>
            <span className={'grid h-11 w-11 place-items-center rounded-full bg-primary/10 text-primary'}>
              <Lock className={'h-5 w-5'} />
            </span>

            <p className={'text-sm text-muted-foreground'}>{translate('movementShare.enterPin')}</p>
          </div>

          <Input
            autoFocus
            inputMode={'numeric'}
            onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
            onKeyDown={e => e.key === 'Enter' && submitPin()}
            placeholder={translate('movementShare.pinPlaceholder')}
            value={pin}
          />

          {locked ? (
            <p className={'text-center text-[13px] text-destructive'}>{translate('movementShare.locked')}</p>
          ) : invalidPin ? (
            <p className={'text-center text-[13px] text-destructive'}>
              {translate('movementShare.invalidPin', { attempts: result?.attemptsLeft ?? 0 })}
            </p>
          ) : null}

          <Button
            className={'w-full'}
            disabled={pin.length === 0}
            isLoading={fetchMovements.isLoading}
            onClick={submitPin}
            type={'primary'}
          >
            {translate('movementShare.view')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Shell accountName={meta.accountName} label={meta.label}>
      {fetchMovements.isLoading && !result ? (
        <div className={'grid place-items-center py-16'}>
          <Spinner />
        </div>
      ) : result?.ok ? (
        <div className={'space-y-3'}>
          <p className={'text-sm text-muted-foreground'}>
            {translate('movementShare.count', { count: result.movements?.length ?? 0 })}
          </p>

          <div className={'overflow-x-auto rounded-lg border border-border'}>
            <table className={'w-full text-sm'}>
              <thead>
                <tr className={'border-b border-border bg-muted/40 text-left'}>
                  {columns.map(column => (
                    <th
                      className={cn(
                        'px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground',
                        column.numeric && 'text-right'
                      )}
                      key={column.key}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {(result.movements ?? []).map(movement => (
                  <tr className={'border-b border-border/60 last:border-0'} key={movement.id}>
                    {columns.map(column => (
                      <td
                        className={cn('whitespace-nowrap px-3 py-2 tabular-nums', column.numeric && 'text-right')}
                        key={column.key}
                      >
                        {renderCell(column.key, movement, coinId)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </Shell>
  );
}

/**
 * Render a single cell by field key.
 */

function renderCell(key: string, movement: any, coinId?: string | null): React.ReactNode {
  switch (key) {
    case 'date':
      return shortDate(movement.date);
    case 'description':
      return <span className={'whitespace-normal'}>{movement.description ?? '—'}</span>;
    case 'category':
      return movement.category ?? '—';
    case 'amount':
      return (
        <span className={cn(Number(movement.amount) < 0 ? 'text-destructive' : 'text-emerald-600')}>
          {money(movement.amount, coinId)}
        </span>
      );
    case 'previousBalance':
      return money(movement.previousBalance, coinId);
    case 'balance':
      return money(movement.balance, coinId);
    default:
      return '—';
  }
}

/**
 * Standalone page shell (no dashboard chrome).
 */

function Shell({
  children,
  accountName,
  label
}: {
  children: React.ReactNode;
  accountName?: string | null;
  label?: string | null;
}): JSX.Element {
  const [translate] = useTranslation();

  return (
    <div className={'min-h-screen bg-muted/30 py-8'}>
      <div className={'mx-auto w-full max-w-4xl px-4'}>
        <SectionHeader
          className={'mb-4'}
          description={accountName ?? undefined}
          icon={<Landmark className={'h-5 w-5'} />}
          size={'lg'}
          title={label || accountName || translate('movementShare.title')}
        />

        <div className={'rounded-xl border border-border bg-card p-4 shadow-sm'}>{children}</div>

        <p className={'mt-4 text-center text-[11px] text-muted-foreground'}>{translate('movementShare.readOnly')}</p>
      </div>
    </div>
  );
}
